import { useState, useMemo, useCallback, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router';
import { ProgressBar } from '@lms/ui';
import {
  CheckCircle2,
  FileText,
  Volume2,
  Video,
  Loader2,
  ArrowLeft,
  Lock,
  BookOpen,
  Play,
  HelpCircle,
  ShieldAlert,
  Award,
  Globe,
  ArrowRight,
  Search,
  X,
  ChevronDown,
  Clock3,
  ListChecks,
} from 'lucide-react';
import { useProgramEnrollment } from '../hooks/useEnrollments';
import type {
  Material,
  ProgramLevel,
  LearningFlow,
  Evaluation,
  ProgramStage,
  ProgramSubject,
  LessonSession,
} from '@lms/shared/types';
import { resolveMediaUrl } from '@lms/shared/utils';
import { useLanguage } from '../lib/LanguageContext';
import { Bilingual } from '../components/Bilingual';
import { copy } from '../copy/copy';
import { apiBaseUrl } from '../lib/api-client';
import { CurriculumPath, type CurriculumPathItem } from '../components/CurriculumPath';

export function ProgramDetail() {
  const { id } = useParams<{ id: string }>();
  const { data: enrollment, isLoading, error } = useProgramEnrollment(id || '');
  const { t } = useLanguage();
  const { hash } = useLocation();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStageCode, setSelectedStageCode] = useState<string>('ALL');
  const [expandedSessionId, setExpandedSessionId] = useState<string | null>(null);
  const [expandedSubjectIds, setExpandedSubjectIds] = useState<string[] | null>(null);
  const [visibleSessionCounts, setVisibleSessionCounts] = useState<Record<string, number>>({});

  const program = enrollment?.program;

  useEffect(() => {
    if (isLoading || !hash) return;
    const frame = window.requestAnimationFrame(() => {
      document
        .getElementById(hash.slice(1))
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [hash, isLoading, program?.id]);

  // Extract unique stage codes across program
  const stageCodes = useMemo(() => {
    const set = new Set<string>();
    program?.levels?.forEach(lvl => {
      lvl.stages?.forEach(stg => {
        if (stg.stageCode) set.add(stg.stageCode);
      });
    });
    return Array.from(set).sort();
  }, [program?.levels]);

  const subjectNavigation = useMemo(() => {
    const items: Array<{
      id: string;
      stageCode?: string | null;
      subjectName: string;
      hasIncompleteMaterial: boolean;
    }> = [];

    program?.levels?.forEach(level => {
      level.stages?.forEach(stage => {
        if (selectedStageCode !== 'ALL' && stage.stageCode !== selectedStageCode) return;
        stage.subjects?.forEach(subject => {
          items.push({
            id: subject.id,
            stageCode: stage.stageCode,
            subjectName: subject.name,
            hasIncompleteMaterial:
              subject.sessions?.some(session =>
                session.materials?.some(material => {
                  const completed = enrollment?.completions?.some(
                    completion => completion.materialId === material.id
                  );
                  const recognized =
                    enrollment?.priorLearningRecognition?.creditedMaterialIds.includes(material.id);
                  return !completed && !recognized;
                })
              ) ?? false,
          });
        });
      });
    });

    return items;
  }, [
    enrollment?.completions,
    enrollment?.priorLearningRecognition,
    program?.levels,
    selectedStageCode,
  ]);

  const initialSubjectId = useMemo(() => {
    if (hash.startsWith('#subject-')) return hash.replace('#subject-', '');

    if (hash.startsWith('#session-')) {
      const requestedSessionId = hash.replace('#session-', '');
      for (const item of subjectNavigation) {
        const subject = program?.levels
          ?.flatMap(level => level.stages || [])
          .flatMap(stage => stage.subjects || [])
          .find(candidate => candidate.id === item.id);
        if (subject?.sessions?.some(session => session.id === requestedSessionId))
          return subject.id;
      }
    }

    return (
      subjectNavigation.find(
        subject =>
          subject.hasIncompleteMaterial &&
          enrollment?.priorLearningRecognition?.accessGrants?.some(
            grant => grant.subjectId === subject.id
          )
      )?.id ??
      subjectNavigation.find(subject => subject.hasIncompleteMaterial)?.id ??
      subjectNavigation[0]?.id ??
      null
    );
  }, [
    enrollment?.priorLearningRecognition?.accessGrants,
    hash,
    program?.levels,
    subjectNavigation,
  ]);

  useEffect(() => {
    if (!hash || !initialSubjectId) return;
    setExpandedSubjectIds(current => {
      const expanded = current ?? [];
      return expanded.includes(initialSubjectId) ? expanded : [...expanded, initialSubjectId];
    });
  }, [hash, initialSubjectId]);

  const toggleSubject = useCallback((subjectId: string) => {
    setExpandedSubjectIds(current => {
      const expanded = current ?? [];
      return expanded.includes(subjectId)
        ? expanded.filter(id => id !== subjectId)
        : [...expanded, subjectId];
    });
  }, []);

  const jumpToSubject = useCallback((subjectId: string) => {
    setExpandedSubjectIds(current => {
      const expanded = current ?? [];
      return expanded.includes(subjectId) ? expanded : [...expanded, subjectId];
    });
    window.requestAnimationFrame(() => {
      document.getElementById(`subject-${subjectId}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  }, []);

  const isCompleted = (materialId: string) => {
    return enrollment?.completions?.some(c => c.materialId === materialId) ?? false;
  };

  const isRecognizedFromPriorLearning = (materialId: string) =>
    enrollment?.priorLearningRecognition?.creditedMaterialIds.includes(materialId) ?? false;

  const isLearningSatisfied = (materialId: string) =>
    isCompleted(materialId) || isRecognizedFromPriorLearning(materialId);

  const getAccessGrant = (subjectId: string) =>
    enrollment?.priorLearningRecognition?.accessGrants?.find(
      grant => grant.subjectId === subjectId
    );

  const isSubjectRecognized = (subject: ProgramSubject) => {
    const materials = subject.sessions?.flatMap(session => session.materials || []) ?? [];
    return (
      materials.length > 0 &&
      materials.every(material => isRecognizedFromPriorLearning(material.id))
    );
  };

  const isPassed = (evaluationId: string) => {
    if (enrollment?.priorLearningRecognition?.qualifiedEvaluationIds.includes(evaluationId)) {
      return true;
    }
    return (
      enrollment?.results?.some(r => {
        const meta = r.metadata as Record<string, unknown> | undefined;
        const rUnknown = r as unknown as Record<string, unknown>;
        const resultEvalId =
          (meta?.evaluationId as string | undefined) ||
          (rUnknown.evaluationId as string | undefined);
        const resultPassed =
          meta?.isPassed !== undefined
            ? (meta.isPassed as boolean)
            : (rUnknown.isPassed as boolean);
        return resultEvalId === evaluationId && resultPassed;
      }) ?? false
    );
  };

  const getEvaluationResult = useCallback(
    (evaluationId: string) => {
      const result = enrollment?.results?.find(r => {
        const meta = r.metadata as Record<string, unknown> | undefined;
        const rUnknown = r as unknown as Record<string, unknown>;
        const resultEvalId =
          (meta?.evaluationId as string | undefined) ||
          (rUnknown.evaluationId as string | undefined);
        return resultEvalId === evaluationId;
      });

      if (!result) return null;
      const meta = result.metadata as Record<string, unknown> | undefined;
      const rUnknown = result as unknown as Record<string, unknown>;
      const passed =
        meta?.isPassed !== undefined ? Boolean(meta.isPassed) : Boolean(rUnknown.isPassed);
      const score =
        result.score ??
        (meta?.score as number | undefined) ??
        (rUnknown.score as number | undefined);

      return {
        isPassed: passed,
        score: score !== undefined ? Math.round(Number(score)) : null,
        attemptedAt: result.createdAt,
      };
    },
    [enrollment]
  );

  // Map of all subjects for prerequisite lookup
  const allSubjectsMap = useMemo(() => {
    const map = new Map<string, ProgramSubject>();
    program?.levels?.forEach(lvl => {
      lvl.stages?.forEach(stg => {
        stg.subjects?.forEach(sbj => {
          map.set(sbj.id, sbj);
        });
      });
    });
    return map;
  }, [program?.levels]);

  // Aggregate all evaluations across the complete 6-tier hierarchy.
  const allProgramEvaluations = useMemo(() => {
    const list: Array<{
      id: string;
      title: string;
      type: 'SESSION' | 'SUBJECT' | 'STAGE' | 'LEVEL' | 'PROGRAM';
      passingScore: number;
      contextLabel: string;
    }> = [];

    program?.levels?.forEach((lvl, lvlIdx) => {
      lvl.stages?.forEach(stg => {
        stg.subjects?.forEach(subject => {
          subject.evaluations?.forEach(ev => {
            list.push({
              id: ev.id,
              title: ev.title,
              type: 'SUBJECT',
              passingScore: ev.passingScore ?? subject.passingScore ?? 70,
              contextLabel: `Mata Pelajaran • ${subject.name}`,
            });
          });

          subject.sessions?.forEach(session => {
            session.evaluations?.forEach(ev => {
              list.push({
                id: ev.id,
                title: ev.title,
                type: 'SESSION',
                passingScore: ev.passingScore ?? subject.passingScore ?? 70,
                contextLabel: `Pertemuan ${session.sessionNumber} • ${subject.name}`,
              });
            });
          });
        });

        stg.evaluations?.forEach(ev => {
          list.push({
            id: ev.id,
            title: ev.title,
            type: 'STAGE',
            passingScore: ev.passingScore ?? stg.passingScore ?? 70,
            contextLabel: `Tahap ${stg.stageCode || ''} • ${stg.name}`,
          });
        });
      });

      lvl.evaluations?.forEach(ev => {
        list.push({
          id: ev.id,
          title: ev.title,
          type: 'LEVEL',
          passingScore: ev.passingScore ?? 70,
          contextLabel: `Level ${lvlIdx + 1} • ${lvl.name}`,
        });
      });
    });

    program?.evaluations?.forEach(ev => {
      list.push({
        id: ev.id,
        title: ev.title,
        type: 'PROGRAM',
        passingScore: ev.passingScore ?? 70,
        contextLabel: 'Evaluasi Akhir Program',
      });
    });

    return list;
  }, [program]);

  const evalStats = useMemo(() => {
    const total = allProgramEvaluations.length;
    let completed = 0;
    let passed = 0;

    allProgramEvaluations.forEach(ev => {
      const res = getEvaluationResult(ev.id);
      if (res) {
        completed++;
        if (res.isPassed) passed++;
      }
    });

    const percentCompleted = total > 0 ? Math.round((completed / total) * 100) : 0;
    const percentPassed = total > 0 ? Math.round((passed / total) * 100) : 0;

    return { total, completed, passed, percentCompleted, percentPassed };
  }, [allProgramEvaluations, getEvaluationResult]);

  const getLevelMaterials = (lvl: ProgramLevel): Material[] => {
    return [
      ...(lvl.flows?.flatMap(f => f.materials || []) || []),
      ...(lvl.stages
        ?.flatMap(s => s.subjects || [])
        .flatMap(sub => sub.sessions || [])
        .flatMap(ses => ses.materials || []) || []),
    ];
  };

  const getLevelLockStatus = (levelIndex: number): { isLocked: boolean; reason?: string } => {
    if (levelIndex === 0) return { isLocked: false };
    const level = program?.levels?.[levelIndex];
    if (level && enrollment?.priorLearningRecognition?.continuationLevelIds?.includes(level.id)) {
      return { isLocked: false };
    }
    const precedingLevels = program?.levels?.slice(0, levelIndex) ?? [];
    const placementOrder = Math.max(
      0,
      ...precedingLevels
        .filter(previous =>
          enrollment?.priorLearningRecognition?.continuationLevelIds?.includes(previous.id)
        )
        .map(previous => previous.order)
    );
    // Match server placement and normal progression; the placed level's own
    // materials and final evaluation still gate progression beyond it.
    for (const prevLevel of precedingLevels) {
      if (prevLevel.order < placementOrder) continue;
      const prevMaterials = getLevelMaterials(prevLevel);
      const allPrevMaterialsDone = prevMaterials.every(m => isLearningSatisfied(m.id));
      if (!allPrevMaterialsDone) {
        return {
          isLocked: true,
          reason: `Selesaikan seluruh materi pada ${prevLevel.name} terlebih dahulu`,
        };
      }

      if (prevLevel.evaluations && prevLevel.evaluations.length > 0) {
        const allPrevEvalsPassed = prevLevel.evaluations.every(e => isPassed(e.id));
        if (!allPrevEvalsPassed) {
          return {
            isLocked: true,
            reason: `Luluskan Evaluasi Akhir ${prevLevel.name} (KKM: ${prevLevel.evaluations[0]?.passingScore ?? 70}%) terlebih dahulu`,
          };
        }
      }
    }
    return { isLocked: false };
  };

  const getSubjectLockStatus = (
    subject: ProgramSubject,
    levelLock: { isLocked: boolean; reason?: string }
  ): { isLocked: boolean; reason?: string } => {
    if (getAccessGrant(subject.id) || isSubjectRecognized(subject)) return { isLocked: false };
    if (levelLock.isLocked) return levelLock;

    if (subject.prerequisiteSubjectId) {
      const prereq = allSubjectsMap.get(subject.prerequisiteSubjectId);
      if (prereq) {
        const prereqMaterials = prereq.sessions?.flatMap(s => s.materials || []) || [];
        const allPrereqDone =
          prereqMaterials.length > 0 && prereqMaterials.every(m => isLearningSatisfied(m.id));
        if (!allPrereqDone) {
          return {
            isLocked: true,
            reason: `Prasyarat belum tuntas: Selesaikan mata pelajaran "${prereq.name}" terlebih dahulu`,
          };
        }
      }
    }

    return { isLocked: false };
  };

  const getSessionLockStatus = (
    session: LessonSession,
    subject: ProgramSubject,
    stage: ProgramStage,
    subjectLock: { isLocked: boolean; reason?: string }
  ): { isLocked: boolean; reason?: string } => {
    if (getAccessGrant(subject.id)?.unlockAllSessions || isSubjectRecognized(subject))
      return { isLocked: false };
    if (subjectLock.isLocked) return subjectLock;

    // Rule 2: Sequential within subject / stage
    if (
      stage.isSequential ||
      subject.isSequential ||
      subject.requireEvaluationPassForNextMaterial
    ) {
      const prevSessions = (subject.sessions || []).filter(
        s => s.sessionNumber < session.sessionNumber
      );
      const prevMaterials = prevSessions.flatMap(s => s.materials || []);
      const allPrevDone = prevMaterials.every(m => isLearningSatisfied(m.id));
      if (!allPrevDone) {
        return {
          isLocked: true,
          reason: `Selesaikan Pertemuan #${session.sessionNumber - 1} terlebih dahulu`,
        };
      }

      if (subject.requireEvaluationPassForNextMaterial) {
        const requiredEvaluations = prevSessions.flatMap(previousSession => [
          ...(previousSession.evaluations || []),
          ...(previousSession.materials || []).flatMap(material => material.evaluations || []),
        ]);
        const pendingEvaluation = requiredEvaluations.find(evaluation => !isPassed(evaluation.id));

        if (pendingEvaluation) {
          return {
            isLocked: true,
            reason: t(copy.programDetail.evaluationGateReason)
              .replace('{evaluation}', pendingEvaluation.title)
              .replace('{score}', String(pendingEvaluation.passingScore ?? 70)),
          };
        }
      }
    }

    // Rule 1: Max 3 Parallel Active Materials Quota for flexible stages
    if (!stage.isSequential && !subject.isSequential && !getAccessGrant(subject.id)) {
      const sessionMaterials = session.materials || [];
      const hasIncompleteMaterial = sessionMaterials.some(
        material => !isLearningSatisfied(material.id)
      );
      if (hasIncompleteMaterial) {
        const stageSessions = stage.subjects?.flatMap(s => s.sessions || []) || [];
        const stageMaterials = stageSessions.flatMap(s => s.materials || []);
        const incompleteMaterials = stageMaterials.filter(m => !isLearningSatisfied(m.id));
        const activeWindow = incompleteMaterials.slice(0, 3);
        const isInActiveWindow = sessionMaterials.some(material =>
          activeWindow.some(activeMaterial => activeMaterial.id === material.id)
        );
        if (!isInActiveWindow && activeWindow.length >= 3) {
          return {
            isLocked: true,
            reason: 'Kuota belajar paralel penuh (maksimal 3 materi aktif)',
          };
        }
      }
    }

    return { isLocked: false };
  };

  const getMaterialLockStatus = (
    materialIndex: number,
    session: LessonSession,
    subject: ProgramSubject,
    stage: ProgramStage
  ): { isLocked: boolean; reason?: string } => {
    const material = session.materials?.[materialIndex];
    if (
      getAccessGrant(subject.id)?.unlockAllSessions ||
      (material && isRecognizedFromPriorLearning(material.id))
    )
      return { isLocked: false };
    if (
      !stage.isSequential &&
      !subject.isSequential &&
      !subject.requireEvaluationPassForNextMaterial
    ) {
      return { isLocked: false };
    }

    const precedingMaterials = (session.materials || []).slice(0, materialIndex);
    const incompleteMaterial = precedingMaterials.find(item => !isLearningSatisfied(item.id));
    if (incompleteMaterial) {
      return {
        isLocked: true,
        reason: t(copy.programDetail.previousMaterialReason).replace(
          '{material}',
          incompleteMaterial.title
        ),
      };
    }

    if (subject.requireEvaluationPassForNextMaterial) {
      const pendingEvaluation = precedingMaterials
        .flatMap(item => item.evaluations || [])
        .find(evaluation => !isPassed(evaluation.id));
      if (pendingEvaluation) {
        return {
          isLocked: true,
          reason: t(copy.programDetail.evaluationGateReason)
            .replace('{evaluation}', pendingEvaluation.title)
            .replace('{score}', String(pendingEvaluation.passingScore ?? 70)),
        };
      }
    }

    return { isLocked: false };
  };

  const isLocked = (
    material: Material,
    flow: LearningFlow & { materials?: Material[] },
    level: ProgramLevel
  ) => {
    if (!flow?.isSequential && !level?.isSequential) return false;

    // Get all materials in the same flow, sorted by order
    const flowMaterials = [...((flow.materials as Material[]) || [])].sort(
      (a, b) => (a.order || 0) - (b.order || 0)
    );
    const materialIndex = flowMaterials.findIndex(m => m.id === material.id);

    if (materialIndex <= 0) return false;

    // Check if all previous materials in this flow are completed
    for (let i = 0; i < materialIndex; i++) {
      if (!isLearningSatisfied(flowMaterials[i].id)) return true;
    }

    return false;
  };

  const isFlowCompleted = (flow: LearningFlow & { materials?: Material[] }) => {
    const flowMaterials = flow.materials || [];
    if (flowMaterials.length === 0) return true;
    return flowMaterials.every(m => isLearningSatisfied(m.id));
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
        <p className="text-sm font-body text-on-surface-variant">
          <Bilingual {...copy.programDetail.loading} />
        </p>
      </div>
    );
  }

  if (error || !enrollment || !program) {
    return (
      <div className="bg-error-container text-on-error-container p-6 rounded-xl animate-fadeIn">
        <h3 className="font-headline font-bold mb-2">
          <Bilingual {...copy.programDetail.accessError} />
        </h3>
        <p className="text-sm font-body">
          {error instanceof Error ? error.message : t(copy.programDetail.notEnrolled)}
        </p>
        <Link
          to="/programs"
          className="inline-flex items-center gap-2 mt-4 text-sm font-label text-primary hover:underline"
        >
          <ArrowLeft size={16} />
          <Bilingual {...copy.programDetail.backToMyPrograms} />
        </Link>
      </div>
    );
  }

  const totalLevelsCount = program?.levels?.length || 0;
  const totalStagesCount =
    program?.levels?.reduce((acc, l) => acc + (l.stages?.length || 0), 0) || 0;
  const totalSubjectsCount =
    program?.levels?.reduce(
      (acc, l) => acc + (l.stages?.reduce((a, s) => a + (s.subjects?.length || 0), 0) || 0),
      0
    ) || 0;
  const totalSessionsCount =
    program?.levels?.reduce(
      (acc, l) =>
        acc +
        (l.stages?.reduce(
          (a, s) => a + (s.subjects?.reduce((b, sub) => b + (sub.sessions?.length || 0), 0) || 0),
          0
        ) || 0),
      0
    ) || 0;

  const allMaterials = [
    ...(program?.levels?.flatMap(l => l.flows?.flatMap(f => f.materials || []) || []) || []),
    ...(program?.levels?.flatMap(
      l =>
        l.stages
          ?.flatMap(s => s.subjects || [])
          .flatMap(sub => sub.sessions || [])
          .flatMap(ses => ses.materials || []) || []
    ) || []),
    ...(program?.flows?.flatMap(f => f.materials || []) || []),
  ];
  const completedMaterialsCount = allMaterials.filter(m => isLearningSatisfied(m.id)).length;
  const allMaterialsFinished =
    allMaterials.length === 0 || completedMaterialsCount >= allMaterials.length;
  const nextLearningStep = (() => {
    const placementOrder = Math.max(
      0,
      ...(program.levels || [])
        .filter(level =>
          enrollment.priorLearningRecognition?.continuationLevelIds?.includes(level.id)
        )
        .map(level => level.order)
    );
    // Prefer granted subjects and the placed level over optional gap repair
    // in older levels. Every subject, session and quiz gate still runs.
    for (const mode of ['GRANTED', 'CONTINUATION', 'NORMAL'] as const) {
      for (const [levelIndex, level] of (program.levels || []).entries()) {
        if (mode === 'CONTINUATION' && (!placementOrder || level.order < placementOrder)) continue;
        const levelLock = getLevelLockStatus(levelIndex);
        for (const stage of level.stages || []) {
          for (const subject of stage.subjects || []) {
            if (mode === 'GRANTED' && !getAccessGrant(subject.id)) continue;
            const subjectLock = getSubjectLockStatus(subject, levelLock);
            if (subjectLock.isLocked) continue;
            for (const session of subject.sessions || []) {
              if (getSessionLockStatus(session, subject, stage, subjectLock).isLocked) continue;
              const material = session.materials?.find(
                (item, index) =>
                  !isLearningSatisfied(item.id) &&
                  !getMaterialLockStatus(index, session, subject, stage).isLocked
              );
              if (material) return { level, stage, subject, session, material };
            }
          }
        }
      }
    }
    return null;
  })();
  const nextMaterial =
    nextLearningStep?.material ??
    allMaterials.find(item => {
      if (item.sessionId || isLearningSatisfied(item.id)) return false;
      const level = program.levels?.find(candidate =>
        candidate.flows?.some(flow => flow.id === item.flowId)
      );
      const flow = level?.flows?.find(candidate => candidate.id === item.flowId);
      return flow && level ? !isLocked(item, flow, level) : Boolean(item.flowId);
    });
  const nextPathItems: CurriculumPathItem[] = nextLearningStep
    ? [
        {
          key: 'program',
          label: t(copy.curriculum.program),
          value: program.name,
          href: `/programs/${program.id}`,
        },
        {
          key: 'level',
          label: t(copy.curriculum.level),
          value: nextLearningStep.level.name,
          href: `#level-${nextLearningStep.level.id}`,
        },
        {
          key: 'stage',
          label: t(copy.curriculum.stage),
          value: nextLearningStep.stage.name,
          href: `#stage-${nextLearningStep.stage.id}`,
        },
        {
          key: 'subject',
          label: t(copy.curriculum.subject),
          value: nextLearningStep.subject.name,
          href: `#subject-${nextLearningStep.subject.id}`,
        },
        {
          key: 'session',
          label: t(copy.curriculum.session),
          value: `#${nextLearningStep.session.sessionNumber} ${nextLearningStep.session.title}`,
          href: `#session-${nextLearningStep.session.id}`,
        },
        {
          key: 'material',
          label: t(copy.curriculum.material),
          value: nextLearningStep.material.title,
          href: `/materials/${nextLearningStep.material.id}`,
          current: true,
        },
      ]
    : [];

  return (
    <div className="space-y-8 animate-fadeIn pb-12">
      {/* Header / Cover Banner */}
      {program?.coverImage ? (
        <div className="relative rounded-[2rem] overflow-hidden aspect-[21/9] sm:aspect-[24/7] max-h-64 w-full shadow-level2 bg-surface-container-low">
          <img
            src={resolveMediaUrl(program.coverImage, apiBaseUrl)}
            alt={program.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-transparent flex flex-col justify-end p-6 sm:p-8 text-white">
            <span className="font-mono text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-white/20 backdrop-blur-xs w-fit mb-2">
              {program.code}
            </span>
            <h1 className="text-2xl sm:text-4xl font-headline font-bold text-white mb-1 tracking-tight">
              {program.name}
            </h1>
            <p className="text-xs sm:text-sm text-white/80 line-clamp-1 max-w-2xl">
              {program.description || t(copy.common.noDescription)}
            </p>
          </div>
        </div>
      ) : (
        <div>
          <h1 className="text-3xl font-headline font-bold text-on-surface mb-2">{program?.name}</h1>
          <p className="text-on-surface-variant">
            {program?.description || t(copy.common.noDescription)}
          </p>
        </div>
      )}

      {/* Overall Progress & Quick Stats Card */}
      <div className="space-y-5 rounded-3xl bg-surface-container-lowest p-6 shadow-level1">
        <div className="flex items-center justify-between">
          <span className="text-xs font-label font-bold text-on-surface-variant uppercase tracking-wider">
            <Bilingual {...copy.programDetail.progressTitle} />
          </span>
          <span className="text-2xl font-headline font-bold text-primary">
            {Math.round(enrollment?.progress || 0)}%
          </span>
        </div>
        <ProgressBar value={enrollment?.progress || 0} />

        {/* Quick Stats Pills */}
        <div className="grid grid-cols-2 gap-2 rounded-2xl bg-surface-container-low p-2 sm:grid-cols-5">
          <div className="bg-surface-container-low/50 p-2.5 rounded-xl text-center">
            <span className="text-[10px] text-on-surface-variant uppercase font-bold block">
              Level
            </span>
            <span className="text-sm font-headline font-bold text-on-surface">
              {totalLevelsCount}
            </span>
          </div>
          <div className="bg-surface-container-low/50 p-2.5 rounded-xl text-center">
            <span className="text-[10px] text-on-surface-variant uppercase font-bold block">
              Tahapan
            </span>
            <span className="text-sm font-headline font-bold text-on-surface">
              {totalStagesCount}
            </span>
          </div>
          <div className="bg-surface-container-low/50 p-2.5 rounded-xl text-center">
            <span className="text-[10px] text-on-surface-variant uppercase font-bold block">
              Mapel
            </span>
            <span className="text-sm font-headline font-bold text-on-surface">
              {totalSubjectsCount}
            </span>
          </div>
          <div className="bg-surface-container-low/50 p-2.5 rounded-xl text-center">
            <span className="text-[10px] text-on-surface-variant uppercase font-bold block">
              Pertemuan
            </span>
            <span className="text-sm font-headline font-bold text-on-surface">
              {totalSessionsCount}
            </span>
          </div>
          <div className="bg-success/10 p-2.5 rounded-xl text-center col-span-2 sm:col-span-1">
            <span className="text-[10px] text-success uppercase font-bold block">Tuntas</span>
            <span className="text-sm font-headline font-bold text-success">
              {completedMaterialsCount}/{totalSessionsCount || allMaterials.length}
            </span>
          </div>
        </div>
      </div>

      {nextMaterial && (
        <section className="space-y-3" aria-labelledby="next-learning-title">
          {nextPathItems.length > 0 && (
            <CurriculumPath
              ariaLabel={t(copy.curriculum.ariaLabel)}
              eyebrow={t(copy.curriculum.nextPosition)}
              items={nextPathItems}
            />
          )}
          <Link
            to={`/materials/${nextMaterial.id}`}
            className="group flex flex-col justify-between gap-5 rounded-3xl bg-primary p-5 text-on-primary no-underline shadow-level2 transition-colors hover:bg-primary-container focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-[0.99] sm:flex-row sm:items-center sm:p-6"
          >
            <div className="min-w-0">
              <p className="m-0 text-[10px] font-label font-bold uppercase tracking-[0.16em] text-secondary">
                <Bilingual {...copy.programDetail.nextMaterial} />
              </p>
              <h2
                id="next-learning-title"
                className="mt-2 line-clamp-2 break-words text-xl font-headline font-bold leading-snug sm:text-2xl"
              >
                {nextMaterial.title}
              </h2>
              {nextMaterial.description && (
                <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-on-primary/80">
                  {nextMaterial.description}
                </p>
              )}
            </div>
            <span className="inline-flex shrink-0 items-center gap-2 self-start whitespace-nowrap rounded-xl bg-on-primary/10 px-4 py-3 text-xs font-label font-bold uppercase tracking-wider sm:self-auto">
              <Bilingual {...copy.programDetail.continueLearning} />
              <ArrowRight size={16} aria-hidden="true" />
            </span>
          </Link>
        </section>
      )}

      {/* Informative Evaluation Progress Card */}
      {evalStats.total > 0 && (
        <div className="bg-gradient-to-r from-secondary/10 via-surface-container-lowest to-surface-container-lowest rounded-3xl p-6 shadow-level1 border border-secondary/20 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-secondary text-on-secondary flex items-center justify-center shadow-sm">
                <Award size={22} />
              </div>
              <div>
                <span className="text-[10px] font-black uppercase tracking-wider text-secondary bg-secondary/10 px-2.5 py-0.5 rounded-full border border-secondary/20">
                  Evaluasi Tahapan & KKM
                </span>
                <h3 className="text-base font-headline font-bold text-on-surface m-0 mt-0.5">
                  Progres Evaluasi & Ujian Tahap
                </h3>
              </div>
            </div>

            <div className="flex items-center gap-4 text-xs">
              <div className="text-right">
                <span className="text-[10px] text-on-surface-variant uppercase font-bold block">
                  Sudah Mengerjakan
                </span>
                <span className="font-mono font-bold text-on-surface">
                  {evalStats.completed}/{evalStats.total} Evaluasi ({evalStats.percentCompleted}%)
                </span>
              </div>
              <div className="h-8 w-px bg-outline-variant/20" />
              <div className="text-right">
                <span className="text-[10px] text-success uppercase font-bold block">
                  Lulus KKM
                </span>
                <span className="font-mono font-bold text-success">
                  {evalStats.passed}/{evalStats.total} ({evalStats.percentPassed}%)
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <div className="flex justify-between text-[11px] font-bold">
              <span className="text-on-surface-variant">Penyelesaian Evaluasi Kurikulum</span>
              <span className="text-secondary font-mono">{evalStats.percentCompleted}%</span>
            </div>
            <ProgressBar value={evalStats.percentCompleted} variant="primary" size="sm" />
          </div>
        </div>
      )}

      {/* Toolbar: Real-time Search & Stage Filter Tabs */}
      <div className="space-y-4 rounded-3xl bg-surface-container-lowest p-4 shadow-level1 sm:p-5">
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
          {/* Live Search */}
          <div className="relative flex-1">
            <label
              htmlFor="program-material-search"
              className="mb-2 block text-xs font-label font-bold uppercase tracking-wider text-on-surface-variant"
            >
              <Bilingual {...copy.programDetail.searchPlaceholder} />
            </label>
            <input
              id="program-material-search"
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder={t(copy.programDetail.searchPlaceholder)}
              className="w-full rounded-xl bg-surface-container-low py-3 pl-10 pr-11 text-sm font-body text-on-surface placeholder:text-on-surface-variant/60 outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            />
            <Search
              size={17}
              className="pointer-events-none absolute left-3.5 top-[calc(50%+0.75rem)] -translate-y-1/2 text-on-surface-variant/60"
              aria-hidden="true"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-[calc(50%+0.75rem)] flex size-8 -translate-y-1/2 items-center justify-center rounded-lg text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-on-surface focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95"
                aria-label={t(copy.programDetail.clearSearch)}
              >
                <X size={16} aria-hidden="true" />
              </button>
            )}
          </div>

          {/* Stage Filter Pills */}
          {stageCodes.length > 1 && (
            <div
              className="flex max-w-full items-center gap-1.5 overflow-x-auto pb-1"
              role="tablist"
            >
              <button
                type="button"
                onClick={() => setSelectedStageCode('ALL')}
                role="tab"
                aria-selected={selectedStageCode === 'ALL'}
                className={`whitespace-nowrap rounded-xl px-3 py-1.5 text-xs font-bold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95 ${
                  selectedStageCode === 'ALL'
                    ? 'bg-primary text-on-primary shadow-sm'
                    : 'bg-surface-container-low text-on-surface-variant hover:bg-surface-container'
                }`}
              >
                <Bilingual {...copy.programDetail.allStages} />
              </button>
              {stageCodes.map(code => (
                <button
                  key={code}
                  type="button"
                  onClick={() => setSelectedStageCode(code)}
                  role="tab"
                  aria-selected={selectedStageCode === code}
                  className={`whitespace-nowrap rounded-xl px-3 py-1.5 text-xs font-bold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95 ${
                    selectedStageCode === code
                      ? 'bg-primary text-on-primary shadow-sm'
                      : 'bg-surface-container-low text-on-surface-variant hover:bg-surface-container'
                  }`}
                >
                  {t(copy.programDetail.stageLabel)} {code}
                </button>
              ))}
            </div>
          )}
        </div>

        {subjectNavigation.length > 1 && (
          <div className="max-w-2xl">
            <label
              htmlFor="program-subject-jump"
              className="mb-2 block text-xs font-label font-bold uppercase tracking-wider text-on-surface-variant"
            >
              <Bilingual {...copy.programDetail.subjectJumpLabel} />
            </label>
            <select
              id="program-subject-jump"
              defaultValue=""
              onChange={event => {
                if (!event.target.value) return;
                jumpToSubject(event.target.value);
                event.target.value = '';
              }}
              className="min-h-11 w-full rounded-xl bg-surface-container-low px-3 text-base font-body text-on-surface outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              <option value="">{t(copy.programDetail.subjectJumpPlaceholder)}</option>
              {subjectNavigation.map(subject => (
                <option key={subject.id} value={subject.id}>
                  {subject.stageCode
                    ? `${t(copy.programDetail.stageLabel)} ${subject.stageCode} · `
                    : ''}
                  {subject.subjectName}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Levels */}
      <div className="space-y-8">
        {program?.levels?.map((level, index) => {
          const levelLock = getLevelLockStatus(index);
          const hasSpecificAccess = level.stages?.some(stage =>
            stage.subjects?.some(
              subject => getAccessGrant(subject.id) || isSubjectRecognized(subject)
            )
          );
          const allLevelMaterials = getLevelMaterials(level);
          const allLevelMaterialsDone =
            allLevelMaterials.length > 0 && allLevelMaterials.every(m => isLearningSatisfied(m.id));

          // Filter stages by selected tab
          const visibleStages = (level.stages || []).filter(stg => {
            if (selectedStageCode === 'ALL') return true;
            return stg.stageCode === selectedStageCode;
          });

          return (
            <div id={`level-${level.id}`} key={level.id} className="scroll-mt-24 space-y-4">
              {/* Level Header */}
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div
                    className={`flex items-center justify-center w-9 h-9 rounded-2xl font-headline font-bold text-sm shadow-sm ${
                      levelLock.isLocked
                        ? 'bg-surface-container-high text-on-surface-variant/50'
                        : 'bg-primary text-on-primary shadow-level1'
                    }`}
                  >
                    {levelLock.isLocked ? <Lock size={15} /> : index + 1}
                  </div>
                  <div>
                    <span className="text-[9px] font-label font-black text-primary uppercase tracking-[0.2em] block">
                      Level / Tingkat {index + 1}
                    </span>
                    <h2 className="text-lg font-headline font-bold text-on-surface mt-0.5">
                      {level.name}
                    </h2>
                  </div>
                </div>

                {levelLock.isLocked && (
                  <span className="flex items-center gap-1.5 rounded-full bg-warning-container px-3 py-1 text-[10px] font-bold text-on-warning-container">
                    <Lock size={12} />
                    <span>Terkunci</span>
                  </span>
                )}
              </div>

              {/* Level Lock Warning Banner */}
              {enrollment.priorLearningRecognition?.continuationLevelIds?.includes(level.id) && (
                <div className="flex items-start gap-3 rounded-2xl bg-primary/10 p-4 text-on-surface">
                  <BookOpen size={20} className="mt-0.5 shrink-0 text-primary" aria-hidden="true" />
                  <p className="text-base leading-relaxed">
                    <Bilingual {...copy.programDetail.archivedLevelContinuation} />
                  </p>
                </div>
              )}
              {levelLock.isLocked && (
                <div className="flex items-center gap-3 rounded-2xl bg-warning-container p-4 text-xs text-on-warning-container">
                  <ShieldAlert size={18} className="shrink-0" />
                  <div className="space-y-2">
                    <span className="font-bold">{levelLock.reason}</span>
                    {hasSpecificAccess && (
                      <p className="text-sm leading-relaxed">
                        <Bilingual {...copy.programDetail.partialLearningAccess} />
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Stages / Subjects / Sessions List */}
              {visibleStages && visibleStages.length > 0 && (
                <div className="space-y-6 ml-0 md:ml-12 mb-6">
                  {visibleStages.map(stage => {
                    const allStageMaterials =
                      stage.subjects?.flatMap(
                        s => s.sessions?.flatMap(ses => ses.materials || []) || []
                      ) || [];
                    const allStageMaterialsDone =
                      allStageMaterials.length > 0 &&
                      allStageMaterials.every(m => isLearningSatisfied(m.id));

                    return (
                      <div
                        id={`stage-${stage.id}`}
                        key={stage.id}
                        className="scroll-mt-24 space-y-5 rounded-3xl bg-surface-container-lowest p-5 shadow-level1 md:p-6"
                      >
                        {/* Stage Header */}
                        <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-outline-variant/10">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold text-xs shadow-sm">
                              {stage.stageCode || 'T'}
                            </div>
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-[10px] font-black uppercase tracking-wider text-primary">
                                  {stage.stageCode ? `Tahap ${stage.stageCode}` : 'Tahapan'}
                                </span>
                                <span
                                  className={`px-2 py-0.5 text-[8px] font-black uppercase rounded-full ${
                                    stage.isSequential
                                      ? 'bg-primary/10 text-primary border border-primary/20'
                                      : 'bg-warning-container text-on-warning-container'
                                  }`}
                                >
                                  {stage.isSequential ? 'Berurutan' : 'Paralel (Maks. 3 Aktif)'}
                                </span>
                                <span className="rounded-full bg-secondary-container px-2 py-0.5 text-[8px] font-bold uppercase text-on-secondary-container">
                                  KKM Tahap: {stage.passingScore ?? 70}%
                                </span>
                              </div>
                              <h3 className="text-sm md:text-base font-headline font-bold text-on-surface mt-0.5">
                                {stage.name}
                              </h3>
                            </div>
                          </div>

                          {stage.description && (
                            <p className="text-[11px] text-on-surface-variant/70 w-full">
                              {stage.description}
                            </p>
                          )}
                        </div>

                        {/* Subjects */}
                        <div className="space-y-4">
                          {stage.subjects?.map(subject => {
                            const subjectLock = getSubjectLockStatus(subject, levelLock);
                            const subjectEvaluations = subject.evaluations || [];
                            const subjectEvaluationMatchesSearch = searchQuery
                              ? subjectEvaluations.some(evaluation =>
                                  evaluation.title.toLowerCase().includes(searchQuery.toLowerCase())
                                )
                              : false;

                            // Filter sessions by search query
                            const sessions = (subject.sessions || []).filter(ses => {
                              if (!searchQuery) return true;
                              const q = searchQuery.toLowerCase();
                              return (
                                subject.name.toLowerCase().includes(q) ||
                                subject.code?.toLowerCase().includes(q) ||
                                ses.title.toLowerCase().includes(q) ||
                                String(ses.sessionNumber).includes(q) ||
                                ses.materials?.some(material =>
                                  material.title.toLowerCase().includes(q)
                                ) ||
                                ses.evaluations?.some(evaluation =>
                                  evaluation.title.toLowerCase().includes(q)
                                )
                              );
                            });
                            const subjectMaterials = (subject.sessions || []).flatMap(
                              session => session.materials || []
                            );
                            const subjectMaterialsCompleted =
                              subjectMaterials.length > 0 &&
                              subjectMaterials.every(material => isLearningSatisfied(material.id));
                            const isSubjectExpanded =
                              Boolean(searchQuery) ||
                              (expandedSubjectIds === null
                                ? subject.id === initialSubjectId
                                : expandedSubjectIds.includes(subject.id));
                            const visibleSessionCount = searchQuery
                              ? sessions.length
                              : Math.min(visibleSessionCounts[subject.id] ?? 8, sessions.length);
                            const visibleSessions = sessions.slice(0, visibleSessionCount);
                            const remainingSessionsCount = sessions.length - visibleSessionCount;
                            const subjectSummary = t(copy.programDetail.subjectSummary)
                              .replace('{sessions}', String(subject.sessions?.length ?? 0))
                              .replace('{materials}', String(subjectMaterials.length));

                            if (
                              searchQuery &&
                              sessions.length === 0 &&
                              !subjectEvaluationMatchesSearch
                            ) {
                              return null;
                            }

                            return (
                              <div
                                id={`subject-${subject.id}`}
                                key={subject.id}
                                className="scroll-mt-24 space-y-5 rounded-3xl bg-surface-container-low p-5 md:p-6"
                              >
                                <div className="flex flex-wrap items-start justify-between gap-4">
                                  <div className="min-w-0 space-y-2">
                                    <div className="flex flex-wrap items-center gap-2.5">
                                      <BookOpen size={18} className="shrink-0 text-primary" />
                                      <h4 className="min-w-0 text-base font-headline font-bold leading-snug text-on-surface md:text-lg">
                                        {subject.name}
                                      </h4>
                                      {subject.code && (
                                        <span className="rounded-lg bg-surface-container-high px-2.5 py-1 font-mono text-xs font-bold text-on-surface-variant">
                                          {subject.code}
                                        </span>
                                      )}
                                      <span
                                        className={`rounded-full px-2.5 py-1 text-xs font-bold ${
                                          subject.isSequential
                                            ? 'bg-primary/10 text-primary'
                                            : 'bg-warning-container text-on-warning-container'
                                        }`}
                                      >
                                        {subject.isSequential ? 'Berurutan' : 'Fleksibel'}
                                      </span>
                                      {subject.requireEvaluationPassForNextMaterial && (
                                        <span className="rounded-full bg-secondary-container px-2.5 py-1 text-xs font-bold text-on-secondary-container">
                                          <Bilingual {...copy.programDetail.evaluationGateBadge} />
                                        </span>
                                      )}
                                    </div>
                                    {enrollment.priorLearningRecognition?.recognizedSubjects
                                      ?.filter(item => item.subjectId === subject.id)
                                      .map(item => (
                                        <p
                                          key={item.subjectId}
                                          className="max-w-prose rounded-xl bg-success-container p-3 text-base leading-relaxed text-primary"
                                        >
                                          {t(copy.programDetail.priorLearningSource)
                                            .replace('{score}', String(item.score))
                                            .replace('{source}', item.sourceTitle)}
                                          {item.scoreIsEstimated && (
                                            <span className="mt-1 block text-sm">
                                              {t(copy.programDetail.priorLearningEstimated)}
                                            </span>
                                          )}
                                        </p>
                                      ))}
                                    {getAccessGrant(subject.id) && (
                                      <p className="max-w-prose rounded-xl bg-secondary-container p-3 text-base leading-relaxed text-on-secondary-container">
                                        <Bilingual {...copy.programDetail.specialLearningAccess} />
                                      </p>
                                    )}
                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-on-surface-variant">
                                      {subject.kitabRujukan && (
                                        <span>Kitab: {subject.kitabRujukan}</span>
                                      )}
                                      {subject.teacherName && (
                                        <span>• Pengajar: {subject.teacherName}</span>
                                      )}
                                    </div>
                                  </div>

                                  <div className="flex flex-wrap items-center gap-2">
                                    {subjectLock.isLocked && (
                                      <span className="flex min-h-11 items-center gap-2 rounded-xl bg-warning-container px-3 py-2 text-sm font-bold text-on-warning-container">
                                        <Lock size={15} />
                                        <span>
                                          <Bilingual
                                            {...copy.programDetail.prerequisiteIncomplete}
                                          />
                                        </span>
                                      </span>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => toggleSubject(subject.id)}
                                      aria-expanded={isSubjectExpanded}
                                      aria-controls={`subject-content-${subject.id}`}
                                      className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-surface-container-high px-3 py-2 text-sm font-bold text-on-surface transition-colors hover:bg-surface-container-highest focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-[0.99]"
                                    >
                                      {isSubjectExpanded
                                        ? t(copy.programDetail.collapseSubject)
                                        : t(copy.programDetail.expandSubject)}
                                      <ChevronDown
                                        size={18}
                                        className={`transition-transform ${
                                          isSubjectExpanded ? 'rotate-180' : ''
                                        }`}
                                        aria-hidden="true"
                                      />
                                    </button>
                                  </div>
                                </div>

                                {!isSubjectExpanded && (
                                  <p className="m-0 text-sm font-medium text-on-surface-variant">
                                    {subjectSummary}
                                  </p>
                                )}

                                <div
                                  id={`subject-content-${subject.id}`}
                                  className={isSubjectExpanded ? 'space-y-5' : 'hidden'}
                                >
                                  {subjectLock.isLocked && (
                                    <p className="rounded-xl bg-warning-container p-3 text-sm font-medium leading-relaxed text-on-warning-container">
                                      {subjectLock.reason}
                                    </p>
                                  )}

                                  {subjectEvaluations.length > 0 && (
                                    <div className="space-y-3 rounded-2xl bg-secondary-container p-4">
                                      <p className="m-0 text-xs font-label font-bold uppercase tracking-wider text-on-secondary-container">
                                        <Bilingual {...copy.programDetail.subjectEvaluation} />
                                      </p>
                                      {subjectEvaluations.map(evaluation => {
                                        const evaluationPassed = isPassed(evaluation.id);
                                        const evaluationLocked =
                                          subjectLock.isLocked ||
                                          (subjectMaterials.length > 0 &&
                                            !subjectMaterialsCompleted);
                                        const evaluationContent = (
                                          <>
                                            <span className="min-w-0 truncate">
                                              {evaluation.title} ·{' '}
                                              {t(copy.programDetail.passingScore)}{' '}
                                              {evaluation.passingScore}%
                                            </span>
                                            <span className="shrink-0 whitespace-nowrap">
                                              {evaluationPassed
                                                ? t(copy.programDetail.passed)
                                                : evaluationLocked
                                                  ? t(copy.programDetail.subjectExamLockedHint)
                                                  : t(copy.programDetail.startExam)}
                                            </span>
                                          </>
                                        );

                                        return evaluationLocked ? (
                                          <div
                                            key={evaluation.id}
                                            className="flex min-w-0 cursor-not-allowed items-center justify-between gap-3 rounded-xl bg-surface-container-high px-4 py-3 text-sm font-bold text-on-surface-variant"
                                            aria-disabled="true"
                                          >
                                            {evaluationContent}
                                          </div>
                                        ) : (
                                          <Link
                                            key={evaluation.id}
                                            to={`/evaluations/${evaluation.id}`}
                                            className="flex min-h-11 min-w-0 items-center justify-between gap-3 rounded-xl bg-secondary-container px-4 py-3 text-sm font-bold text-on-secondary-container no-underline transition-colors hover:bg-secondary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-[0.99]"
                                          >
                                            {evaluationContent}
                                          </Link>
                                        );
                                      })}
                                    </div>
                                  )}

                                  {/* Sessions List */}
                                  {sessions && sessions.length > 0 ? (
                                    <section
                                      className="space-y-3 pt-1"
                                      aria-label={t(copy.programDetail.sessionListTitle)}
                                    >
                                      <p
                                        className="m-0 text-sm font-medium text-on-surface-variant"
                                        aria-live="polite"
                                      >
                                        {t(copy.programDetail.displayedSessions)
                                          .replace('{shown}', String(visibleSessionCount))
                                          .replace('{total}', String(sessions.length))}
                                      </p>
                                      {visibleSessions.map(session => {
                                        const sessionLock = getSessionLockStatus(
                                          session,
                                          subject,
                                          stage,
                                          subjectLock
                                        );
                                        const sessionMaterials = session.materials || [];
                                        const sessionEvaluations = session.evaluations || [];
                                        const sessionCompleted =
                                          sessionMaterials.length > 0 &&
                                          sessionMaterials.every(item =>
                                            isLearningSatisfied(item.id)
                                          );
                                        const sessionMeetingUrl =
                                          session.meetingUrl ||
                                          sessionMaterials.find(item => item.meetingUrl)
                                            ?.meetingUrl;
                                        const hashSessionId = hash.startsWith('#session-')
                                          ? hash.replace('#session-', '')
                                          : null;
                                        const isSessionExpanded =
                                          expandedSessionId === session.id ||
                                          (expandedSessionId === null &&
                                            (hashSessionId === session.id ||
                                              nextLearningStep?.session.id === session.id));

                                        if (sessionLock.isLocked) {
                                          return (
                                            <div
                                              id={`session-${session.id}`}
                                              key={session.id}
                                              className="scroll-mt-24 rounded-2xl bg-surface-container p-4 md:p-5"
                                              aria-disabled="true"
                                            >
                                              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                                <div className="flex min-w-0 items-start gap-3">
                                                  <Lock
                                                    size={18}
                                                    className="mt-0.5 shrink-0 text-on-surface-variant"
                                                  />
                                                  <div className="min-w-0">
                                                    <p className="m-0 break-words text-base font-bold leading-snug text-on-surface-variant">
                                                      #{session.sessionNumber} {session.title}
                                                    </p>
                                                    <p className="mt-1 text-sm leading-relaxed text-on-surface-variant">
                                                      {sessionLock.reason ||
                                                        t(copy.programDetail.sessionLockedHint)}
                                                    </p>
                                                  </div>
                                                </div>
                                                <span className="w-fit rounded-full bg-surface-container-highest px-3 py-1.5 text-xs font-bold text-on-surface-variant">
                                                  <Bilingual {...copy.programDetail.locked} />
                                                </span>
                                              </div>
                                            </div>
                                          );
                                        }

                                        return (
                                          <div
                                            id={`session-${session.id}`}
                                            key={session.id}
                                            className={`scroll-mt-24 space-y-4 rounded-3xl bg-surface-container-lowest p-5 shadow-level1 md:p-6 ${
                                              nextLearningStep?.session.id === session.id
                                                ? 'shadow-level2'
                                                : ''
                                            }`}
                                          >
                                            <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                              <button
                                                type="button"
                                                onClick={() =>
                                                  setExpandedSessionId(
                                                    isSessionExpanded ? '' : session.id
                                                  )
                                                }
                                                aria-expanded={isSessionExpanded}
                                                aria-controls={`session-content-${session.id}`}
                                                className="flex min-h-11 min-w-0 flex-1 items-start gap-3 rounded-2xl px-2 py-2 text-left transition-colors hover:bg-surface-container-low focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-[0.99]"
                                              >
                                                {sessionCompleted ? (
                                                  <CheckCircle2
                                                    size={20}
                                                    className="mt-0.5 shrink-0 text-success"
                                                  />
                                                ) : (
                                                  <Play
                                                    size={18}
                                                    className="mt-0.5 shrink-0 fill-primary/10 text-primary"
                                                  />
                                                )}
                                                <div className="min-w-0">
                                                  <span className="block break-words text-base font-bold leading-snug text-on-surface md:text-lg">
                                                    #{session.sessionNumber} {session.title}
                                                  </span>
                                                  {sessionMaterials.length === 0 &&
                                                    sessionEvaluations.length > 0 && (
                                                      <span className="mt-2 inline-flex rounded-full bg-secondary-container px-2.5 py-1 text-xs font-bold text-on-secondary-container">
                                                        <Bilingual
                                                          {...copy.programDetail
                                                            .evaluationOnlySession}
                                                        />
                                                      </span>
                                                    )}
                                                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-on-surface-variant">
                                                    {session.duration && (
                                                      <span className="inline-flex items-center gap-1.5">
                                                        <Clock3 size={15} aria-hidden="true" />
                                                        {session.duration} mnt
                                                      </span>
                                                    )}
                                                    <span className="inline-flex items-center gap-1.5">
                                                      <BookOpen size={15} aria-hidden="true" />
                                                      {sessionMaterials.length}{' '}
                                                      {t(copy.curriculum.material)}
                                                    </span>
                                                    <span className="inline-flex items-center gap-1.5">
                                                      <ListChecks size={15} aria-hidden="true" />
                                                      {sessionEvaluations.length}{' '}
                                                      {t(copy.curriculum.evaluation)}
                                                    </span>
                                                  </div>
                                                </div>
                                                <span className="ml-auto inline-flex shrink-0 items-center gap-2 self-center whitespace-nowrap text-sm font-bold text-primary">
                                                  {isSessionExpanded
                                                    ? t(copy.programDetail.closeSession)
                                                    : t(copy.curriculum.openSession)}
                                                  <ChevronDown
                                                    size={20}
                                                    className={`shrink-0 text-on-surface-variant transition-transform ${
                                                      isSessionExpanded ? 'rotate-180' : ''
                                                    }`}
                                                    aria-hidden="true"
                                                  />
                                                </span>
                                              </button>
                                              {sessionMeetingUrl && (
                                                <a
                                                  href={sessionMeetingUrl}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-xl bg-success/10 text-success no-underline transition-colors hover:bg-success/20 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95"
                                                  aria-label={t(copy.dashboard.joinLiveSession)}
                                                >
                                                  <Globe size={18} />
                                                </a>
                                              )}
                                            </div>

                                            {isSessionExpanded && (
                                              <div
                                                id={`session-content-${session.id}`}
                                                className="space-y-3 rounded-2xl bg-surface-container-low p-4"
                                              >
                                                <p className="m-0 text-xs font-label font-bold uppercase tracking-wider text-on-surface-variant">
                                                  <Bilingual
                                                    {...copy.programDetail.sessionContents}
                                                  />
                                                </p>
                                                {sessionMaterials.map((material, materialIndex) => {
                                                  const materialCompleted = isLearningSatisfied(
                                                    material.id
                                                  );
                                                  const materialLock = getMaterialLockStatus(
                                                    materialIndex,
                                                    session,
                                                    subject,
                                                    stage
                                                  );
                                                  const MaterialIcon =
                                                    material.type === 'VIDEO'
                                                      ? Video
                                                      : material.type === 'AUDIO'
                                                        ? Volume2
                                                        : material.type === 'QUIZ'
                                                          ? HelpCircle
                                                          : FileText;
                                                  const materialContent = (
                                                    <>
                                                      <span className="flex min-w-0 items-center gap-2 truncate">
                                                        {materialLock.isLocked ? (
                                                          <Lock size={14} className="shrink-0" />
                                                        ) : (
                                                          <MaterialIcon
                                                            size={14}
                                                            className="shrink-0"
                                                          />
                                                        )}
                                                        <span className="truncate">
                                                          {material.order}. {material.title}
                                                        </span>
                                                      </span>
                                                      <span className="shrink-0 whitespace-nowrap">
                                                        {materialLock.isLocked
                                                          ? t(copy.programDetail.locked)
                                                          : materialCompleted
                                                            ? t(copy.programDetail.openAgain)
                                                            : t(copy.programDetail.startMaterial)}
                                                      </span>
                                                    </>
                                                  );

                                                  return materialLock.isLocked ? (
                                                    <div
                                                      key={material.id}
                                                      title={materialLock.reason}
                                                      aria-disabled="true"
                                                      className="flex min-w-0 cursor-not-allowed items-center justify-between gap-3 rounded-xl bg-surface-container-high px-4 py-3 text-sm font-bold text-on-surface-variant"
                                                    >
                                                      {materialContent}
                                                    </div>
                                                  ) : (
                                                    <Link
                                                      key={material.id}
                                                      to={`/materials/${material.id}`}
                                                      className={`flex min-h-11 min-w-0 items-center justify-between gap-3 rounded-xl px-4 py-3 text-sm font-bold no-underline transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-[0.99] ${
                                                        materialCompleted
                                                          ? 'bg-surface-container-lowest text-on-surface hover:bg-surface-container-high'
                                                          : 'bg-primary text-on-primary hover:bg-primary-container'
                                                      }`}
                                                    >
                                                      {materialContent}
                                                    </Link>
                                                  );
                                                })}

                                                {sessionEvaluations.map(evaluation => {
                                                  const evaluationPassed = isPassed(evaluation.id);
                                                  const evaluationLocked =
                                                    sessionMaterials.length > 0 &&
                                                    !sessionCompleted;
                                                  const evaluationContent = (
                                                    <>
                                                      <span className="min-w-0 truncate">
                                                        {evaluation.title} ·{' '}
                                                        {t(copy.programDetail.passingScore)}{' '}
                                                        {evaluation.passingScore}%
                                                      </span>
                                                      <span className="shrink-0 whitespace-nowrap">
                                                        {evaluationPassed
                                                          ? t(copy.programDetail.passed)
                                                          : evaluationLocked
                                                            ? t(copy.programDetail.examLockedHint)
                                                            : t(copy.programDetail.startExam)}
                                                      </span>
                                                    </>
                                                  );

                                                  return evaluationLocked ? (
                                                    <div
                                                      key={evaluation.id}
                                                      className="flex min-w-0 cursor-not-allowed items-center justify-between gap-3 rounded-xl bg-surface-container-high px-4 py-3 text-sm font-bold text-on-surface-variant"
                                                      aria-disabled="true"
                                                    >
                                                      {evaluationContent}
                                                    </div>
                                                  ) : (
                                                    <Link
                                                      key={evaluation.id}
                                                      to={`/evaluations/${evaluation.id}`}
                                                      className="flex min-h-11 min-w-0 items-center justify-between gap-3 rounded-xl bg-secondary-container px-4 py-3 text-sm font-bold text-on-secondary-container no-underline transition-colors hover:bg-secondary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-[0.99]"
                                                    >
                                                      {evaluationContent}
                                                    </Link>
                                                  );
                                                })}

                                                {sessionMaterials.length === 0 &&
                                                  sessionEvaluations.length === 0 && (
                                                    <p className="m-0 py-2 text-sm text-on-surface-variant">
                                                      <Bilingual
                                                        {...copy.programDetail.noSessionContent}
                                                      />
                                                    </p>
                                                  )}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                      {remainingSessionsCount > 0 && !searchQuery && (
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setVisibleSessionCounts(current => ({
                                              ...current,
                                              [subject.id]:
                                                visibleSessionCount +
                                                Math.min(8, remainingSessionsCount),
                                            }))
                                          }
                                          className="flex min-h-11 w-full items-center justify-center rounded-xl bg-surface-container-high px-4 py-3 text-sm font-bold text-primary transition-colors hover:bg-surface-container-highest focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-[0.99]"
                                        >
                                          {t(copy.programDetail.showMoreSessions).replace(
                                            '{count}',
                                            String(Math.min(8, remainingSessionsCount))
                                          )}
                                        </button>
                                      )}
                                    </section>
                                  ) : (
                                    <p className="text-sm text-on-surface-variant italic">
                                      Tidak ada pertemuan yang sesuai dengan pencarian.
                                    </p>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Stage-level Evaluations (Rule 3) */}
                        {stage.evaluations && stage.evaluations.length > 0 && (
                          <div className="pt-3 border-t border-outline-variant/10 space-y-2">
                            {stage.evaluations.map(evaluation => {
                              const evalResult = getEvaluationResult(evaluation.id);
                              const evalPassed = evalResult?.isPassed ?? isPassed(evaluation.id);
                              const evalAttempted = evalResult !== null;
                              const evalLocked = !allStageMaterialsDone || levelLock.isLocked;
                              const passingScore =
                                evaluation.passingScore ?? stage.passingScore ?? 70;

                              return (
                                <div
                                  key={evaluation.id}
                                  className={`p-4 rounded-2xl transition-all border ${
                                    evalPassed
                                      ? 'bg-success/5 border-success/30'
                                      : evalAttempted && !evalPassed
                                        ? 'bg-amber-500/5 border-amber-500/30'
                                        : evalLocked
                                          ? 'bg-surface-container-low/30 border-outline-variant/10 opacity-60'
                                          : 'bg-surface-container-lowest border-secondary shadow-level1 hover:shadow-level2'
                                  }`}
                                >
                                  <div className="flex flex-wrap items-center justify-between gap-3">
                                    <div className="flex items-center gap-3.5">
                                      <div
                                        className={`w-9 h-9 rounded-xl flex items-center justify-center font-bold ${
                                          evalPassed
                                            ? 'bg-success text-white'
                                            : evalAttempted && !evalPassed
                                              ? 'bg-amber-500 text-white'
                                              : evalLocked
                                                ? 'bg-surface-container-high text-on-surface-variant/50'
                                                : 'bg-secondary/15 text-secondary'
                                        }`}
                                      >
                                        {evalPassed ? (
                                          <CheckCircle2 size={18} />
                                        ) : evalLocked ? (
                                          <Lock size={16} />
                                        ) : (
                                          <HelpCircle size={18} />
                                        )}
                                      </div>
                                      <div>
                                        <span className="text-xs font-headline font-bold text-on-surface block">
                                          {evaluation.title}
                                        </span>
                                        <p className="text-[10px] text-secondary font-black uppercase tracking-wider mt-0.5">
                                          Evaluasi Akhir Tahap • KKM: {passingScore}%
                                          {evalAttempted && evalResult?.score !== null && (
                                            <span className="ml-2 font-mono font-bold">
                                              (Nilai Anda: {evalResult?.score}%)
                                            </span>
                                          )}
                                        </p>
                                      </div>
                                    </div>

                                    <div className="flex items-center gap-2">
                                      {evalPassed ? (
                                        <div className="flex items-center gap-2">
                                          <span className="px-3 py-1 bg-success/15 text-success rounded-full text-[10px] font-black uppercase tracking-wider">
                                            ✓ Lulus KKM Tahap ({evalResult?.score ?? 100}%)
                                          </span>
                                          <Link
                                            to={`/evaluations/${evaluation.id}`}
                                            className="text-[10px] font-bold text-on-surface-variant hover:text-primary transition-colors underline"
                                          >
                                            Lihat Hasil
                                          </Link>
                                        </div>
                                      ) : evalAttempted && !evalPassed ? (
                                        <div className="flex items-center gap-2">
                                          <span className="px-2.5 py-1 bg-amber-500/15 text-amber-700 dark:text-amber-300 rounded-full text-[10px] font-black uppercase tracking-wider">
                                            Belum Lulus ({evalResult?.score ?? 0}%)
                                          </span>
                                          <Link
                                            to={`/evaluations/${evaluation.id}`}
                                            className="px-3.5 py-1.5 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-bold text-[10px] uppercase tracking-wider shadow-sm transition-all"
                                          >
                                            Ulangi Ujian Tahap
                                          </Link>
                                        </div>
                                      ) : evalLocked ? (
                                        <span className="text-[10px] text-on-surface-variant/60 font-medium">
                                          Terkunci: Selesaikan semua sesi tahap ini
                                        </span>
                                      ) : (
                                        <Link
                                          to={`/evaluations/${evaluation.id}`}
                                          className="px-4 py-2 rounded-xl bg-secondary text-on-secondary font-bold text-[10px] uppercase tracking-wider shadow-sm hover:opacity-90 transition-opacity inline-block"
                                        >
                                          Mulai Ujian Tahap
                                        </Link>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Level-level Evaluations (Rule 4) */}
              {level.evaluations && level.evaluations.length > 0 && (
                <div className="ml-0 md:ml-12 p-5 bg-surface-container-lowest rounded-3xl border-2 border-secondary/20 shadow-level1 space-y-3">
                  {level.evaluations.map(evaluation => {
                    const evalResult = getEvaluationResult(evaluation.id);
                    const evalPassed = evalResult?.isPassed ?? isPassed(evaluation.id);
                    const evalAttempted = evalResult !== null;
                    const evalLocked = !allLevelMaterialsDone || levelLock.isLocked;
                    const passingScore = evaluation.passingScore ?? 70;

                    return (
                      <div
                        key={evaluation.id}
                        className="flex flex-wrap items-center justify-between gap-4"
                      >
                        <div className="flex items-center gap-3.5">
                          <div
                            className={`w-11 h-11 rounded-2xl flex items-center justify-center font-bold ${
                              evalPassed
                                ? 'bg-success text-white'
                                : evalAttempted && !evalPassed
                                  ? 'bg-amber-500 text-white'
                                  : evalLocked
                                    ? 'bg-surface-container-high text-on-surface-variant/50'
                                    : 'bg-secondary text-on-secondary shadow-level1'
                            }`}
                          >
                            {evalPassed ? (
                              <Award size={22} />
                            ) : evalLocked ? (
                              <Lock size={18} />
                            ) : (
                              <HelpCircle size={22} />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="px-2 py-0.5 bg-secondary/15 text-secondary text-[8px] font-black uppercase tracking-widest rounded-full">
                                Evaluasi Akhir Level
                              </span>
                              <span className="text-[10px] text-on-surface-variant/60 font-bold">
                                KKM: {passingScore}%
                              </span>
                              {evalAttempted && evalResult?.score !== null && (
                                <span className="text-[10px] font-mono font-bold text-primary">
                                  • Nilai: {evalResult?.score}%
                                </span>
                              )}
                            </div>
                            <h4 className="text-sm font-headline font-bold text-on-surface mt-0.5">
                              {evaluation.title}
                            </h4>
                          </div>
                        </div>

                        <div>
                          {evalPassed ? (
                            <div className="flex items-center gap-2">
                              <span className="px-3.5 py-1.5 bg-success/15 text-success rounded-full text-xs font-black uppercase tracking-wider flex items-center gap-1.5">
                                <CheckCircle2 size={14} /> Level {index + 1} Tuntas (
                                {evalResult?.score ?? 100}%)
                              </span>
                              <Link
                                to={`/evaluations/${evaluation.id}`}
                                className="text-xs font-bold text-on-surface-variant hover:text-primary transition-colors underline"
                              >
                                Detail
                              </Link>
                            </div>
                          ) : evalAttempted && !evalPassed ? (
                            <div className="flex items-center gap-2">
                              <span className="px-3 py-1 bg-amber-500/15 text-amber-700 dark:text-amber-300 rounded-full text-xs font-black uppercase tracking-wider">
                                Belum Lulus Level
                              </span>
                              <Link
                                to={`/evaluations/${evaluation.id}`}
                                className="px-4 py-2 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs uppercase tracking-wider shadow-sm transition-all"
                              >
                                Ulangi Ujian Level
                              </Link>
                            </div>
                          ) : evalLocked ? (
                            <span className="text-xs text-on-surface-variant/60 font-medium">
                              Terkunci: Selesaikan seluruh tahapan Level {index + 1}
                            </span>
                          ) : (
                            <Link
                              to={`/evaluations/${evaluation.id}`}
                              className="px-5 py-2.5 rounded-xl bg-secondary text-on-secondary font-bold text-xs uppercase tracking-wider shadow-level2 hover:opacity-90 transition-opacity inline-block"
                            >
                              Mulai Ujian Akhir Level
                            </Link>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Divider between levels */}
              {index < (program.levels?.length || 0) - 1 && (
                <div className="my-8 border-b border-on-surface/5" />
              )}
            </div>
          );
        })}
      </div>

      {/* Top-Level Flows (Legacy programs without levels, or flows not nested in levels) */}
      {program.flows && program.flows.length > 0 && (
        <div className="space-y-6">
          {program.flows
            .filter(
              flow =>
                (flow.materials && flow.materials.length > 0) ||
                (flow.evaluations && flow.evaluations.length > 0)
            )
            .map(flow => (
              <div key={flow.id} className="space-y-3">
                {flow.name && (
                  <h3 className="text-[10px] font-black text-on-surface-variant/40 uppercase tracking-[0.2em] mb-4 mt-8 first:mt-0 flex items-center gap-3">
                    <span className="h-px flex-1 bg-on-surface/5"></span>
                    {flow.name}
                    <span className="h-px flex-1 bg-on-surface/5"></span>
                  </h3>
                )}
                {flow.materials?.map(material => {
                  const completed = isLearningSatisfied(material.id);
                  const recognized = isRecognizedFromPriorLearning(material.id);
                  const locked = isLocked(material, flow, { isSequential: false } as ProgramLevel);
                  const Icon =
                    material.type === 'VIDEO'
                      ? Video
                      : material.type === 'DISCUSSION'
                        ? Volume2
                        : FileText;

                  const content = (
                    <div
                      className={`flex items-start gap-4 p-4 bg-surface-container-lowest rounded-2xl transition-all duration-300 group
                    ${locked ? 'opacity-40 grayscale pointer-events-none' : 'hover:bg-surface-container-low/50 hover:shadow-md hover:scale-[1.01] active:scale-[0.99]'}`}
                    >
                      {/* Material Status Icon */}
                      <div className="flex-shrink-0 mt-1">
                        {locked ? (
                          <Lock size={18} className="text-on-surface-variant/40" />
                        ) : completed ? (
                          <CheckCircle2 size={18} className="text-success" />
                        ) : (
                          <div className="size-5 rounded-lg bg-primary/5 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-on-primary transition-all">
                            <Icon size={12} className="transition-all" />
                          </div>
                        )}
                      </div>

                      {/* Material Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-headline font-bold text-on-surface group-hover:text-primary transition-colors line-clamp-1 text-sm">
                            {material.title}
                          </h4>
                        </div>
                        <p className="text-[10px] font-body text-on-surface-variant/60 mt-1 uppercase tracking-wider">
                          {material.type.toLowerCase()} • {material.duration || 0}{' '}
                          {t(copy.common.minutes).toLowerCase()}
                        </p>
                        {recognized && !isCompleted(material.id) && (
                          <p className="text-[10px] font-bold text-success mt-1">
                            <Bilingual {...copy.programDetail.priorLearningRecognized} />
                          </p>
                        )}
                      </div>

                      {/* Action Hint */}
                      <div className="flex-shrink-0 self-center">
                        <span
                          className={`text-[10px] font-black uppercase tracking-widest transition-all duration-300 ${locked ? 'text-on-surface-variant/20' : 'text-primary opacity-0 group-hover:opacity-100'}`}
                        >
                          {locked ? t(copy.programDetail.locked) : t(copy.programDetail.study)}
                        </span>
                      </div>
                    </div>
                  );

                  return (
                    <div key={material.id} className="space-y-2">
                      {locked ? (
                        content
                      ) : (
                        <Link to={`/materials/${material.id}`} className="no-underline block">
                          {content}
                        </Link>
                      )}

                      {/* Inline Evaluations */}
                      {material.evaluations?.map((evaluation: Evaluation) => {
                        const evalPassed = isPassed(evaluation.id);
                        const evalLocked = !completed;

                        return (
                          <div key={evaluation.id} className="ml-12 mr-4">
                            <Link
                              to={evalLocked ? '#' : `/evaluations/${evaluation.id}`}
                              className={`flex items-center justify-between p-3 rounded-xl transition-all border
                              ${evalLocked ? 'opacity-40 grayscale pointer-events-none bg-surface-container-low/30 border-on-surface/5' : 'bg-surface-container-low border-secondary/10 hover:shadow-sm hover:scale-[1.01]'}`}
                            >
                              <div className="flex items-center gap-3">
                                {evalPassed ? (
                                  <CheckCircle2 size={14} className="text-success" />
                                ) : (
                                  <div className="size-4 rounded flex items-center justify-center bg-secondary/10 text-secondary">
                                    <FileText size={10} />
                                  </div>
                                )}
                                <span className="text-[11px] font-headline font-bold text-on-surface tracking-tight">
                                  {evaluation.title}
                                </span>
                              </div>
                              <span
                                className={`text-[9px] font-black uppercase tracking-widest ${evalPassed ? 'text-success' : 'text-secondary'}`}
                              >
                                {evalLocked
                                  ? t(copy.programDetail.locked)
                                  : evalPassed
                                    ? t(copy.programDetail.passed)
                                    : t(copy.programDetail.quiz)}
                              </span>
                            </Link>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}

                {/* Flow-level Evaluations */}
                {flow.evaluations?.map((evaluation: Evaluation) => {
                  const evalPassed = isPassed(evaluation.id);
                  const evalLocked = !isFlowCompleted(flow);

                  return (
                    <div key={evaluation.id} className="ml-12 mr-4 mt-2">
                      <Link
                        to={evalLocked ? '#' : `/evaluations/${evaluation.id}`}
                        className={`flex items-center justify-between p-3 rounded-xl transition-all border
                        ${evalLocked ? 'opacity-40 grayscale pointer-events-none bg-surface-container-low/30 border-on-surface/5' : 'bg-surface-container-low border-secondary/10 hover:shadow-sm hover:scale-[1.01]'}`}
                      >
                        <div className="flex items-center gap-3">
                          {evalPassed ? (
                            <CheckCircle2 size={14} className="text-success" />
                          ) : (
                            <div className="size-4 rounded flex items-center justify-center bg-secondary/10 text-secondary">
                              <FileText size={10} />
                            </div>
                          )}
                          <span className="text-[11px] font-headline font-bold text-on-surface tracking-tight">
                            {evaluation.title}
                          </span>
                        </div>
                        <span
                          className={`text-[9px] font-black uppercase tracking-widest ${evalPassed ? 'text-success' : 'text-secondary'}`}
                        >
                          {evalLocked
                            ? t(copy.programDetail.locked)
                            : evalPassed
                              ? t(copy.programDetail.passed)
                              : t(copy.programDetail.quiz)}
                        </span>
                      </Link>
                    </div>
                  );
                })}
              </div>
            ))}
        </div>
      )}

      {/* Final Milestones (Exams) */}
      {program.evaluations &&
        program.evaluations.some((e: Evaluation) => !e.materialId && !e.levelId && !e.flowId) && (
          <div className="pt-8 border-t border-on-surface/10">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-secondary/10 rounded-xl text-secondary">
                <CheckCircle2 size={24} />
              </div>
              <div>
                <h2 className="text-xl font-headline font-bold text-on-surface tracking-tight">
                  <Bilingual {...copy.programDetail.finalMilestones} />
                </h2>
                <p className="text-xs text-on-surface-variant font-body uppercase tracking-widest">
                  <Bilingual {...copy.programDetail.assessmentsCompletion} />
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {program.evaluations
                .filter((e: Evaluation) => !e.materialId && !e.levelId && !e.flowId)
                .map(evaluation => {
                  const isLocked = !allMaterialsFinished;

                  return (
                    <div
                      key={evaluation.id}
                      className={`relative p-6 rounded-3xl border transition-all ${
                        isLocked
                          ? 'bg-surface-container-low border-on-surface/5 opacity-60'
                          : 'bg-surface-container-lowest border-secondary/20 shadow-level1 hover:shadow-level2'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="p-3 bg-secondary-container/30 rounded-2xl text-secondary">
                          <FileText size={24} />
                        </div>
                        {isLocked && (
                          <div className="px-3 py-1 bg-on-surface/5 rounded-full text-[10px] font-bold text-on-surface-variant uppercase tracking-tighter">
                            <Bilingual {...copy.programDetail.locked} />
                          </div>
                        )}
                      </div>

                      <h3 className="font-headline font-bold text-lg text-on-surface mb-2">
                        {evaluation.title}
                      </h3>
                      <p className="text-sm text-on-surface-variant mb-6 line-clamp-2">
                        {evaluation.description || t(copy.evaluationDetail.defaultDesc)}
                      </p>

                      <div className="flex items-center justify-between mt-auto">
                        <div className="flex flex-col">
                          <span className="text-[10px] text-on-surface-variant uppercase font-bold tracking-widest">
                            <Bilingual {...copy.programDetail.passingScore} />
                          </span>
                          <span className="text-sm font-bold text-primary">
                            {evaluation.passingScore}%
                          </span>
                        </div>

                        {isLocked ? (
                          <div className="text-xs font-body text-on-surface-variant italic">
                            <Bilingual {...copy.programDetail.unlockHint} />
                          </div>
                        ) : (
                          <Link
                            to={`/evaluations/${evaluation.id}`}
                            className="inline-flex items-center gap-2 py-2 px-6 bg-secondary text-on-secondary rounded-full font-bold text-sm hover:scale-105 active:scale-95 transition-all shadow-lg shadow-secondary/20"
                          >
                            <Bilingual {...copy.programDetail.startExam} />
                          </Link>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}
    </div>
  );
}
